import { Link, createFileRoute, type ErrorComponentProps } from '@tanstack/react-router'
import { useState } from 'react'

import RouteErrorPage from '@/components/layout/RouteErrorPage'
import ScreenLayout from '@/components/layout/ScreenLayout'
import DatePicker, { type DatePickerValue } from '@/components/ui/DatePicker'
import EmptyState from '@/components/ui/EmptyState'
import { stateActionClass } from '@/components/ui/empty-state-variants'
import SegmentedControl from '@/components/ui/SegmentedControl'
import RefreshRegion from '@/components/ui/RefreshRegion'
import TitleRow from '@/components/ui/TitleRow'
import UpdatingNote from '@/components/ui/UpdatingNote'
import { useBrokersByStock } from '@/features/brokers/api/queries'
import { useOnline } from '@/hooks/useOnline'
import { countInclusiveDays } from '@/lib/bsCalendarMath'
import { formatDate } from '@/lib/format'
import { lastUpdatedMeta, resolveViewState } from '@/lib/view-state'
import { BROKER_PERIODS, type BrokerDateRange, type BrokerPeriod } from '@/schemas/broker'
import {
  BY_STOCK_MAX_RANGE_DAYS,
  byStockSearchSchema,
  toByStockRange,
} from '@/screens/brokers/search'
import { loadByStock } from '@/screens/brokers/ByStockScreen.loader'
import { PERIOD_OPTIONS } from '@/screens/brokers/components/broker/broker-format'
import StockPicker from '@/screens/brokers/components/broker/StockPicker'
import WindowNote from '@/screens/brokers/components/broker/WindowNote'
import {
  ByStockBodySkeleton,
  ByStockSkeleton,
} from '@/screens/brokers/components/by-stock/ByStockStates'
import HolderViews from '@/screens/brokers/components/by-stock/HolderViews'
import PriceHead from '@/screens/brokers/components/by-stock/PriceHead'
import StockBrokerTable from '@/screens/brokers/components/by-stock/StockBrokerTable'
import StockHolderTable from '@/screens/brokers/components/by-stock/StockHolderTable'

/** Known without data, so the pending and failed pages draw the real one. */
const HEADER = <TitleRow title="By Stock" />

function ByStockPending() {
  const { symbol } = Route.useParams()
  return (
    <ScreenLayout
      pending
      pendingLabel={`Loading ${symbol}’s broker activity…`}
      header={HEADER}
      pageTitle="By Stock"
    >
      <ByStockSkeleton />
    </ScreenLayout>
  )
}

/**
 * Light on purpose (docs/route-loading.md, "Error components"): only
 * `RouteErrorPage` plus the static header, so it stays in the entry chunk.
 * `STOCK_NOT_FOUND` never
 * reaches here any more — `brokersByStockQueryOptions` resolves it to
 * `data === null` instead of throwing, and the screen's own body draws that
 * branch (below), the way Stock draws `company === null`. This component
 * only ever shows for a genuine failure: offline, or the request itself
 * broke.
 */
function ByStockError(props: ErrorComponentProps) {
  return (
    <RouteErrorPage {...props} header={HEADER} title="Couldn't load this stock's broker activity" />
  )
}

export const Route = createFileRoute('/_app/brokers/by-stock/$symbol')({
  validateSearch: byStockSearchSchema,
  loader: loadByStock,
  pendingComponent: ByStockPending,
  errorComponent: ByStockError,
  component: ByStockScreen,
})

// See `broker-format.ts` — the shared, `BROKER_PERIODS`-derived list, in
// place of a third hand-duplicated literal.

/** The date panel's view of the URL's window: one day when the two ends meet. */
const toPickerDay = (range: BrokerDateRange | null): DatePickerValue =>
  range === null
    ? { kind: 'today' }
    : range.start === range.end
      ? { kind: 'day', date: range.start }
      : { kind: 'range', from: range.start, to: range.end }

/**
 * Brokers · By stock.
 *
 * `ScreenLayout` with the shared rail; the section strip names the page, so the
 * body opens on the controls — the stock picker, the period chips and the
 * calendar button — then the price head, Most bought and Most sold STACKED, each
 * the full main column, "Who is left holding", its bar panel and the footnote.
 *
 * The calendar button sits here and nowhere else in the family: By stock's
 * endpoint is the only one that takes a custom `start`/`end`. The range lives in
 * the URL beside `period`, and wins over it, so the chips show no selection while
 * one is set.
 */
function ByStockScreen() {
  const { symbol } = Route.useParams()
  const search = Route.useSearch()
  const { period } = search
  const range = toByStockRange(search)
  const navigate = Route.useNavigate()
  const online = useOnline()
  const [rangeError, setRangeError] = useState<string | null>(null)

  const query = useBrokersByStock(symbol, period, range)
  // `null` means STOCK_NOT_FOUND, caught in `brokersByStockQueryOptions`'s
  // own `queryFn` rather than thrown (docs/route-loading.md, "Not-found
  // answers") — checked below as its own branch, the same shape Stock's
  // `company === null` uses.
  const data = query.data
  /**
   * `keepPreviousData` holds `query.isLoading` FALSE across a period change, so
   * the skeleton branch below — which is the screen's ONLY loading story — is
   * skipped entirely and the previous period's tables stay painted, unmarked,
   * for the whole fetch. `isPlaceholderData` is what separates "this is the
   * period you asked for" from "this is the period you left".
   */
  const isUpdating = query.isPlaceholderData
  // `data` on screen wins over a failed background refetch, and an offline
  // first load reads as offline rather than loading forever — see
  // `resolveViewState`'s own doc comment. Coerced to `undefined` for the
  // `null` (not-found) case, which is handled as its own branch below and
  // must not read as "loaded".
  const view = resolveViewState({ ...query, data: data ?? undefined }, { online })

  // A chip click drops the range (it is a different question, and the URL holds
  // one or the other).
  const onPeriodChange = (next: BrokerPeriod) => {
    setRangeError(null)
    void navigate({ search: { period: next } })
  }

  const onRangeChange = (next: BrokerDateRange) => {
    if (countInclusiveDays(next.start, next.end) > BY_STOCK_MAX_RANGE_DAYS) {
      setRangeError('Pick a range of up to a year.')
      return
    }
    setRangeError(null)
    void navigate({ search: { period, start: next.start, end: next.end } })
  }

  // The window carries over to the next stock, range included.
  const onStockChange = (next: string) => {
    void navigate({
      to: '/brokers/by-stock/$symbol',
      params: { symbol: next },
      search: { period, ...(range === null ? {} : range) },
    })
  }

  return (
    <ScreenLayout header={HEADER} pageTitle="By Stock">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <StockPicker symbol={symbol} name={data?.name} onPick={onStockChange} />
        {/* The note leads this cluster, which the row's `justify-between` holds
            against the right edge — so it grows leftward into free space and
            the chips do not slide out from under the pointer. */}
        <div className="flex flex-wrap items-center gap-3">
          <UpdatingNote when={isUpdating} label="Updating this period…" />
          {/* The preset row and the calendar square are one control (the mock's `bra-period`):
              the inset segments, then the date panel. This endpoint takes `start`/`end`, so the
              panel's One day (`start = end`) and Date range tabs both apply. No segment is
              selected while a custom day or range is. */}
          <div className="flex items-center gap-3">
            <SegmentedControl
              size="sm"
              skin="inset"
              options={PERIOD_OPTIONS}
              value={range === null ? period : ''}
              onChange={(next) => {
                const chosen = BROKER_PERIODS.find((candidate) => candidate === next)
                if (chosen !== undefined) onPeriodChange(chosen)
              }}
              ariaLabel="Period"
            />
            <DatePicker
              value={toPickerDay(range)}
              onChange={(next) => {
                if (next.kind === 'day') onRangeChange({ start: next.date, end: next.date })
                else if (next.kind === 'range') onRangeChange({ start: next.from, end: next.to })
                else onPeriodChange('today')
              }}
              trigger="icon"
              cta="Show this day"
              rangeAvailable
            />
          </div>
        </div>
      </div>
      {rangeError === null ? null : (
        <p role="alert" className="-mt-3 text-body-xs text-destructive">
          {rangeError}
        </p>
      )}

      {data === null ? (
        <EmptyState
          kind="not-found"
          size="page"
          title="We couldn't find that stock"
          description={`"${symbol}" doesn't match a listed company. Check the symbol and try again.`}
          action={
            <Link to="/market/stock-prices" className={stateActionClass('page')}>
              Browse stock prices
            </Link>
          }
        />
      ) : view === 'failed' || view === 'offline' ? (
        <EmptyState
          kind={view}
          size="page"
          onRetry={() => void query.refetch()}
          retrying={query.isFetching}
          meta={view === 'failed' ? lastUpdatedMeta(query.dataUpdatedAt) : undefined}
          {...(view === 'failed' ? { title: "Couldn't load this stock's broker activity" } : {})}
        />
      ) : view === 'loading' ? (
        <div className="flex flex-col gap-6">
          <ByStockBodySkeleton />
        </div>
      ) : data === undefined ? null : (
        // Kept as they are, never swapped back to the skeletons above:
        // these are real rows the member may still be reading, just the ones
        // for the period they left. Blanking them is the flicker
        // `keepPreviousData` was added to stop.
        <RefreshRegion updating={isUpdating} className="flex flex-col gap-6">
          <PriceHead data={data} />

          {/*
            The SHARED `WindowNote`, not a fourth local wording of the same
            caption: the feature's most important honesty signal ("this month
            is actually one day") reads as a warning banner on every windowed
            screen. A custom range is never partial — it is served from the
            precompute only — so it has no banner, and says which days it
            covers instead: `WindowNote`'s "As of" would name only the last one.
          */}
          {data.window.period === 'custom' ? (
            <p className="text-body-xs text-muted-foreground">
              {data.window.fromDate == null || data.window.toDate == null
                ? 'No trading sessions in this range.'
                : `${formatDate(data.window.fromDate)} to ${formatDate(data.window.toDate)}`}
            </p>
          ) : (
            <WindowNote window={data.window} />
          )}

          {/*
            `isLoading={false}` is not an oversight and is not now wrong: this
            branch only renders once `data` is defined, so from either table's
            point of view the rows it has been handed are always real rows and
            a row-level skeleton would never be correct.

            What changed under it is what "real rows" guarantees. Before
            `keepPreviousData`, reaching here meant the rows matched the period
            chip; now it can also mean they are the previous period's, and
            flipping these to `true` on `isPlaceholderData` would blank both
            tables on every chip tap — the exact flicker the branch's parent was
            changed to prevent. The in-flight signal belongs where the member
            clicked, beside the chips, which is where it now is.
          */}
          <StockBrokerTable
            title="Most bought"
            dot="up"
            rows={data.mostBought}
            isLoading={false}
            emptyTitle={`No broker bought ${data.symbol} in this window`}
          />
          <StockBrokerTable
            title="Most sold"
            dot="down"
            rows={data.mostSold}
            isLoading={false}
            emptyTitle={`No broker sold ${data.symbol} in this window`}
          />

          {/*
            "Who is left holding {symbol}" (Broker/Shares/Amount/Avg buy
            price/Avg sell price). The API now serves `left_holding[]`
            directly (schema regen, 2026-09-25) — no more client-side netting
            of `most_bought`/`most_sold`, which would have been exactly the
            fabricated recompute the sector-flow design already warns against.
            The bar panel under it draws the same rows.
          */}
          <StockHolderTable symbol={data.symbol} rows={data.leftHolding} />
          <HolderViews rows={data.leftHolding} />

          {/*
            The entry point into PV range. This link ALWAYS carries a real
            symbol — it comes off `data.symbol`, not the URL — even though
            `/brokers/pv-range` also has a param-less entry now that falls
            back to the same last-used stock this page just recorded
            (`ByStockScreen.loader.ts`, `lastUsed.ts`). Same axis, one level
            finer: this page is which brokers traded the name, that one is at
            which prices.

            A plain `Link` rather than `ArrowLink`, which takes `to` and
            `search` but has no `params` slot and so cannot express a dynamic
            route — the same limitation `BrokerCell` documents about
            DataTable's `rowTo`. The chevron and the accent tone are
            ArrowLink's, restated here rather than widening a shared primitive
            for one call site.
          */}
          <p>
            <Link
              to="/brokers/pv-range/$symbol"
              params={{ symbol: data.symbol }}
              className="text-accent text-label-l hover:underline"
            >
              See where {data.symbol} traded, by price
              <span aria-hidden="true"> ›</span>
            </Link>
          </p>
        </RefreshRegion>
      )}
    </ScreenLayout>
  )
}
